import { Component, OnInit, AfterViewInit, Output, inject, EventEmitter, signal, ChangeDetectorRef } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { FormGroup, FormArray, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { SelectOptions } from '../../../../core/interfaces/select-options.interface';
import { MatDialog } from '@angular/material/dialog';
import { SignatureDialogComponent } from '../signature-dialog/signature-dialog.component';
import { HttpStatusCode } from '@angular/common/http';
import { NewRepApplicationService } from '../../new-rep-application/new-rep-application-services/new-rep-application.service';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { ConfirmPopupComponent } from '../../new-rep-application/confirm-popup/confirm-popup.component';
import { SessionStorageService } from '../../../../core/services/session-storage/session-storage.service';
import { IntroducerFormCorporateService } from './introducer-form-corporate-service/introducer-form-corporate-service.service';
import { Router } from '@angular/router';
import { UserAuthenticationService } from '../../../../core/services/user-authentication/user-authentication.service';
import { noWhitespace } from '../../../../shared/validators/no-whitespace.validator';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocumentPreviewOverlayDirective } from '../../../../shared/directives/document-preview-overlay/document-preview-overlay.directive';

@Component({
  selector: 'app-introducer-form-corporate',
  imports: [
    MatExpansionModule, MatIcon, ReactiveFormsModule,
    MatFormFieldModule, MatInputModule, MatRadioModule, MatDatepickerModule,
    MatNativeDateModule, MatButtonModule, MatOptionModule, MatSelectModule,
    MatCheckboxModule, CommonModule, MatTooltipModule,
    DocumentPreviewOverlayDirective
  ],
  templateUrl: './introducer-form-corporate.component.html',
  styleUrl: './introducer-form-corporate.component.scss'
})
export class IntroducerFormCorporateComponent implements OnInit, AfterViewInit {
  @Output() next = new EventEmitter<boolean>();

  readonly = signal(false);
  setSprinner = signal(false);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private newRepApplicationService = inject(NewRepApplicationService);
  private snackBarService = inject(SnackbarService);
  private sessionStorageService = inject(SessionStorageService);
  private introducerFormCorporateService = inject(IntroducerFormCorporateService);
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);

  protected nationalities!: SelectOptions[];
  protected countries!: SelectOptions[];
  public message = 'This field is required';
  formDisabled = false;

  private readonly MAX_PHONE_LENGTH = 13;
  private readonly ALLOWED_FILE_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);
  private readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  private applicationId!: number;
  private userRole: string | null = null;
  private detailsLoaded = false;
  data = history.state;

  // inside the class
  private router = inject(Router);
  private userAuthenticationService = inject(UserAuthenticationService);

  private corNationality = '';
  viewIntroducerDetails: any[] = [];
  private loadedChecklist: any[] = [];
  private sectionCId = 0;
  private leadProcessFlowDocId: number | null = null;
  private privacyPolicyDocId: number | null = null;

  /** Prefix used for the dynamically added "Other Attachments" controls. */
  readonly otherAttachmentPrefix = 'otherAttachment';
  /** Document type sent for every "Other Attachments" upload - the row index goes in ResponseValue. */
  readonly otherAttachmentDescription = 'OtherAttachments';
  /** Control keys of the rendered "Other Attachments" rows, e.g. ['otherAttachment1', 'otherAttachment2']. */
  otherAttachmentKeys: string[] = [];
  private otherAttachmentSeq = 0;

  private readonly DOCUMENT_TYPE_LABELS: { [fileVar: string]: string } = {
    attachment_company: 'ACRA BizFile (within last 3 months)',
    attachment_employee: 'Attachment (NRIC / Passport)',
    attachment_addressProof: 'Attachment (Address Proof)',
    attachment_payment: 'Attachment (Bank Account Statement within last 3 months)',
    attachment_leadProcessFlow: 'Process Flow Diagram',
    attachment_privacyPolicyAttachment: 'Privacy Policy / Personal Data Policy'
  };

  private readonly DOCUMENT_TYPE_CONTROL_MAP: { [categoryName: string]: { controlName: string; fileVar: string } } = {
    'ACRA BizFile (within last 3 months)': { controlName: 'companyAttachmentFileName', fileVar: 'attachment_company' },
    'Attachment (NRIC / Passport)': { controlName: 'employeeattachment', fileVar: 'attachment_employee' },
    'Attachment (Address Proof)': { controlName: 'addressProofAttachment', fileVar: 'attachment_addressProof' },
    'Attachment (Bank Account Statement within last 3 months)': { controlName: 'paymentattachment', fileVar: 'attachment_payment' },
    'Process Flow Diagram': { controlName: 'leadProcessFlowAttachment', fileVar: 'attachment_leadProcessFlow' },
    'Privacy Policy / Personal Data Policy': { controlName: 'privacyPolicyAttachment', fileVar: 'attachment_privacyPolicyAttachment' }
  };

  private readonly CHECKLIST_CATEGORY_MAP: { [formControlName: string]: string } = {
    introducerQuestionnaires_1: 'generalInformation_ic_1',
    introducerQuestionnaires_2: 'generalInformation_ic_2',
    introducerQuestionnaires_3: 'generalInformation_ic_3',
    introducerQuestionnaires_4: 'generalInformation_ic_4',
    introducerQuestionnaires_5: 'generalInformation_ic_5',
    introducerQuestionnaires_6: 'generalInformation_ic_6',
    introducerQuestionnaires_7: 'generalInformation_ic_7',
    introducerQuestionnaires_9: 'generalInformation_ic_8',

    introducerQuestionnaires_8: 'fitPropercriteria_ic_1',
    introducerQuestionnaires_10: 'fitPropercriteria_ic_2',
    introducerQuestionnaires_11: 'fitPropercriteria_ic_3',
    introducerQuestionnaires_12: 'fitPropercriteria_ic_4',
    introducerQuestionnaires_13: 'fitPropercriteria_ic_5',
    introducerQuestionnaires_14: 'fitPropercriteria_ic_6',
    introducerQuestionnaires_15: 'fitPropercriteria_ic_7',
    introducerQuestionnaires_16: 'fitPropercriteria_ic_8',
    introducerQuestionnaires_17: 'fitPropercriteria_ic_9',
    introducerQuestionnaires_18: 'fitPropercriteria_ic_10',
    introducerQuestionnaires_19: 'fitPropercriteria_ic_11',
    introducerQuestionnaires_20: 'fitPropercriteria_ic_12',
    introducerQuestionnaires_21: 'fitPropercriteria_ic_13',
    introducerQuestionnaires_22: 'fitPropercriteria_ic_14',
    introducerQuestionnaires_23: 'fitPropercriteria_ic_15',
    introducerQuestionnaires_24: 'fitPropercriteria_ic_16',
    introducerQuestionnaires_25: 'fitPropercriteria_ic_17',
    introducerQuestionnaires_26: 'fitPropercriteria_ic_18',
    introducerQuestionnaires_27: 'fitPropercriteria_ic_19',
    introducerQuestionnaires_28: 'fitPropercriteria_ic_20',
    introducerQuestionnaires_29: 'fitPropercriteria_ic_21',
    introducerQuestionnaires_30: 'fitPropercriteria_ic_22',
    introducerQuestionnaires_31: 'fitPropercriteria_ic_23',
    introducerQuestionnaires_32: 'fitPropercriteria_ic_24',
  };

  private readonly FITPROPER_SUBQUESTION_CATEGORY_MAP: { [questionIndex: number]: string } = {
    8: 'fitPropercriteria_ic_yes_8'
  };

  private readonly REVERSE_FITPROPER_SUBQUESTION_CATEGORY_MAP: { [apiCategoryName: string]: number } =
    Object.fromEntries(
      Object.entries(this.FITPROPER_SUBQUESTION_CATEGORY_MAP).map(([index, apiName]) => [apiName, Number(index)])
    );

  private readonly REVERSE_CHECKLIST_CATEGORY_MAP: { [apiCategoryName: string]: string } =
    Object.fromEntries(
      Object.entries(this.CHECKLIST_CATEGORY_MAP).map(([formControlName, apiName]) => [apiName, formControlName])
    );

  private readonly ALL_QUESTION_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 9, 8, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32];

  public formGroup: FormGroup = this.fb.group({
    companyName: ['', [Validators.required, noWhitespace]],
    companyRegistrationNumber: ['', [Validators.required, noWhitespace]],
    companyIncorporation: ['', [Validators.required, noWhitespace]],
    companyRegisteredAddress: ['', [Validators.required, noWhitespace]],
    natureOfBusiness: ['', [Validators.required, noWhitespace]],

    contactNumber: ['', [Validators.required, Validators.pattern(/^\+?[0-9]{1,13}$/), Validators.maxLength(13)]],

    companyAttachmentFileName: ['', Validators.required],
    attachment_company: [''],

    nricName: ['', [Validators.required, noWhitespace]],
    passportNo: ['', [Validators.required, noWhitespace]],
    designation: ['', [Validators.required, noWhitespace]],
    emailAddress: ['', [Validators.required, Validators.email]],

    contactNo: ['', [Validators.required, Validators.pattern(/^\+?[0-9]{1,13}$/), Validators.maxLength(13)]],

    nationality: ['', Validators.required],
    employeeattachment: ['', Validators.required],
    attachment_employee: [''],
    addressProofAttachment: [''],
    attachment_addressProof: [''],
    address: [''],
    postalCode: [''],

    signature: [''],
    occupation: [''],
    currentEmployer: [''],
    employerAddress: [''],
    buisness: [''],
    attachment_nric: [''],

    payee: ['', [Validators.required, noWhitespace]],
    acNo: ['', [Validators.required, noWhitespace]],
    bankName: ['', [Validators.required, noWhitespace]],
    branchCode: ['', [Validators.required, noWhitespace]],
    paymentattachment: ['', Validators.required],
    attachment_payment: [''],

    introducerQuestionnaires_1: [null, Validators.required],
    introducerQuestionnairesyes_1: ['', [Validators.required, noWhitespace]],
    introducerQuestionnaires_2: [null, Validators.required],
    introducerQuestionnaires_3: [null, Validators.required],
    introducerQuestionnairesyes_3: ['', [Validators.required, noWhitespace]],
    introducerQuestionnaires_4: [null, Validators.required],
    introducerQuestionnaires_5: [null, Validators.required],
    introducerQuestionnairesyes_5: ['', [Validators.required, noWhitespace]],
    introducerQuestionnaires_6: [null, Validators.required],
    introducerQuestionnairesyes_6: ['', [Validators.required, noWhitespace]],
    introducerQuestionnaires_7: [null, Validators.required],
    introducerQuestionnaires_9: [null, Validators.required],

    introducers: this.fb.array([this.createIntroducerRow()]),

    introducerQuestionnaires_8: [null, Validators.required],
    introducerQuestionnairesyes_8: ['', [Validators.required, noWhitespace]],
    introducerQuestionnairesop1_8: [null, Validators.required],

    introducerQuestionnaires_10: [null, Validators.required],
    introducerQuestionnaires_11: [null, Validators.required],
    introducerQuestionnaires_12: [null, Validators.required],
    introducerQuestionnaires_13: [null, Validators.required],
    introducerQuestionnaires_14: [null, Validators.required],
    introducerQuestionnaires_15: [null, Validators.required],
    introducerQuestionnaires_16: [null, Validators.required],
    introducerQuestionnaires_17: [null, Validators.required],
    introducerQuestionnaires_18: [null, Validators.required],
    introducerQuestionnaires_19: [null, Validators.required],
    introducerQuestionnaires_20: [null, Validators.required],
    introducerQuestionnaires_21: [null, Validators.required],
    introducerQuestionnaires_22: [null, Validators.required],
    introducerQuestionnaires_23: [null, Validators.required],
    introducerQuestionnaires_24: [null, Validators.required],
    introducerQuestionnaires_25: [null, Validators.required],
    introducerQuestionnaires_26: [null, Validators.required],
    introducerQuestionnaires_27: [null, Validators.required],
    introducerQuestionnaires_28: [null, Validators.required],
    introducerQuestionnaires_29: [null, Validators.required],
    introducerQuestionnaires_30: [null, Validators.required],
    introducerQuestionnaires_31: [null, Validators.required],
    introducerQuestionnaires_32: [null, Validators.required],

    leadGenExistingClientbase: [false],
    leadGenReferrals: [false],
    leadGenDigitalMarketing: [false],
    leadGenSocialMedia: [false],
    leadGenSocialMediaPlatform: [''], // Starts unvalidated
    leadGenEventsSeminars: [false],
    leadGenOthers: [false],

    leadGenOthersSpecify: [''],
    companyWebsite: [''],
    socialMediaLinks: [''],
    businessActivitiesLeadGen: ['', [Validators.required, noWhitespace]],
    typicalClients: ['', [Validators.required, noWhitespace]],
    leadGenCampaigns: ['', [Validators.required, noWhitespace]],
    leadProcessGenerated: ['', [Validators.required, noWhitespace]],
    leadProcessCollected: ['', [Validators.required, noWhitespace]],
    leadProcessPassedToPias: ['', [Validators.required, noWhitespace]],
    leadProcessFlowAttachment: ['', Validators.required],
    attachment_leadProcessFlow: [''],
    clientInformedMethod: ['', [Validators.required, noWhitespace]],
    consentSharingData: [false],
    consentContact: [false],
    privacyPolicyStatus: [null],
    privacyPolicyAttachment: ['', Validators.required],
    attachment_privacyPolicyAttachment: [''],

    additionalInfo: [''],
    additionalattachment: [''],
    attachment_additional: [''],

    fullName: [''],
    date: [''],
    declarationaccepted: [false],
    declarationName: [''],
  });

  activePanelIndex = -1;
  private isManagerReview = false;

  selectedDocument!: SafeResourceUrl;
  isPdf = false;

  readonly MIN_ZOOM = 0.5;
  readonly MAX_ZOOM = 3;
  private readonly ZOOM_STEP = 0.25;
  zoomLevel = 1;
  showPreview = false;
  currentFileName!: string;

  sectionOrder: string[] = [
    'introducer', 'liaison', 'payment', 'general', 'fitproper', 'sectionC', 'additional', 'declaration'
  ];

  private sectionSavedState: { [key: string]: boolean } = {
    introducer: false, liaison: false, payment: false, general: false,
    fitproper: false, sectionC: false, additional: false, declaration: false
  };

  private sectionFieldsMap: { [key: string]: string[] } = {
    introducer: [
      'companyName', 'companyRegistrationNumber', 'companyIncorporation',
      'companyRegisteredAddress', 'natureOfBusiness', 'contactNumber', 'companyAttachmentFileName'
    ],
    liaison: [
      'nricName', 'passportNo', 'designation', 'emailAddress', 'contactNo',
      'nationality', 'employeeattachment', 'addressProofAttachment'
    ],
    payment: ['payee', 'acNo', 'bankName', 'branchCode', 'paymentattachment'],
    general: [
      'introducerQuestionnaires_1', 'introducerQuestionnairesyes_1',
      'introducerQuestionnaires_2', 'introducerQuestionnaires_3',
      'introducerQuestionnairesyes_3', 'introducerQuestionnaires_4',
      'introducerQuestionnaires_5', 'introducerQuestionnairesyes_5',
      'introducerQuestionnaires_6', 'introducerQuestionnairesyes_6',
      'introducerQuestionnaires_7', 'introducerQuestionnaires_9'
    ],
    fitproper: [
      'introducerQuestionnaires_8', 'introducerQuestionnairesyes_8',
      'introducerQuestionnairesop1_8',
      'introducerQuestionnaires_10', 'introducerQuestionnaires_11',
      'introducerQuestionnaires_12', 'introducerQuestionnaires_13',
      'introducerQuestionnaires_14', 'introducerQuestionnaires_15',
      'introducerQuestionnaires_16', 'introducerQuestionnaires_17',
      'introducerQuestionnaires_18', 'introducerQuestionnaires_19',
      'introducerQuestionnaires_20', 'introducerQuestionnaires_21',
      'introducerQuestionnaires_22', 'introducerQuestionnaires_23',
      'introducerQuestionnaires_24', 'introducerQuestionnaires_25',
      'introducerQuestionnaires_26', 'introducerQuestionnaires_27',
      'introducerQuestionnaires_28', 'introducerQuestionnaires_29',
      'introducerQuestionnaires_30', 'introducerQuestionnaires_31', 'introducerQuestionnaires_32'
    ],
    sectionC: [
      'leadGenExistingClientbase', 'leadGenReferrals', 'leadGenDigitalMarketing',
      'leadGenSocialMedia', 'leadGenSocialMediaPlatform', 'leadGenEventsSeminars',
      'leadGenOthers', 'leadGenOthersSpecify', 'companyWebsite', 'socialMediaLinks',
      'businessActivitiesLeadGen', 'typicalClients', 'leadGenCampaigns',
      'leadProcessGenerated', 'leadProcessCollected', 'leadProcessPassedToPias',
      'leadProcessFlowAttachment', 'clientInformedMethod', 'consentSharingData',
      'consentContact', 'privacyPolicyStatus', 'privacyPolicyAttachment'
    ],
    additional: ['additionalInfo', 'additionalattachment'],
    declaration: ['declarationaccepted', 'declarationName', 'date']
  };

  sectionStatuses = signal<Record<string, 'completed' | 'inprogress' | 'notstarted'>>({
    introducer: 'notstarted', liaison: 'notstarted', payment: 'notstarted',
    general: 'notstarted', fitproper: 'notstarted', sectionC: 'notstarted',
    additional: 'notstarted', declaration: 'notstarted'
  });

  constructor() {
    this.setupAutoUnsaveOnChange();
    this.setupNationalityAddressProofValidation();
    this.setupPrivacyPolicyValidation();
    this.setupSocialMediaAndOthersValidation();
    this.setupConditionalQuestionnaireValidation();
  }

  private setupConditionalQuestionnaireValidation(): void {
    const pairs: { parent: string; child: string }[] = [
      { parent: 'introducerQuestionnaires_1', child: 'introducerQuestionnairesyes_1' },
      { parent: 'introducerQuestionnaires_3', child: 'introducerQuestionnairesyes_3' },
      { parent: 'introducerQuestionnaires_5', child: 'introducerQuestionnairesyes_5' },
      { parent: 'introducerQuestionnaires_6', child: 'introducerQuestionnairesyes_6' },
    ];

    pairs.forEach(({ parent, child }) => {
      const childControl = this.formGroup.get(child);
      this.formGroup.get(parent)?.valueChanges.subscribe((value: boolean | null) => {
        if (this.readonly()) return;
        if (value === true) {
          childControl?.setValidators([Validators.required, noWhitespace]);
        } else {
          childControl?.clearValidators();
          childControl?.setValue('');
        }
        childControl?.updateValueAndValidity();
      });
    });

    const yes8 = this.formGroup.get('introducerQuestionnairesyes_8');
    const op1_8 = this.formGroup.get('introducerQuestionnairesop1_8');
    this.formGroup.get('introducerQuestionnaires_8')?.valueChanges.subscribe((value: boolean | null) => {
      if (this.readonly()) return;
      if (value === true) {
        yes8?.setValidators([Validators.required, noWhitespace]);
        op1_8?.setValidators([Validators.required]);   // tri-state control, no whitespace risk
      } else {
        yes8?.clearValidators();
        yes8?.setValue('');
        op1_8?.clearValidators();
        op1_8?.setValue(null);
      }
      yes8?.updateValueAndValidity();
      op1_8?.updateValueAndValidity();
    });
  }

  private showSubmittedAndLogout(): void {
    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Your Application has been successfully submitted. Currently in Review. No further action required.',
        hidecancel: true,
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result?.data === true) {
        const responseData = await this.userAuthenticationService.userLogout()
          .then((response) => JSON.parse(response))
          .catch((error) => error);

        if (responseData?.status === HttpStatusCode.Ok) {
          this.snackBarService.openSuccessSnackBar('Logged out successfully!');
        } else {
          this.snackBarService.openFailureSnackBar('We couldn\'t process your request. Please try again');
        }

        this.sessionStorageService.clearSessionStorage();
        this.router.navigate(['/login']);
      }
    });
  }

  private setupSocialMediaAndOthersValidation(): void {
    const socialPlatformControl = this.formGroup.get('leadGenSocialMediaPlatform');
    this.formGroup.get('leadGenSocialMedia')?.valueChanges.subscribe((isChecked: boolean) => {
      if (this.readonly()) return;
      if (isChecked) {
        socialPlatformControl?.setValidators([Validators.required]);
      } else {
        socialPlatformControl?.clearValidators();
        socialPlatformControl?.setValue(''); // Reset text when unchecked
      }
      socialPlatformControl?.updateValueAndValidity();
    });

    const othersSpecifyControl = this.formGroup.get('leadGenOthersSpecify');
    this.formGroup.get('leadGenOthers')?.valueChanges.subscribe((isChecked: boolean) => {
      if (this.readonly()) return;
      if (isChecked) {
        othersSpecifyControl?.setValidators([Validators.required]);
      } else {
        othersSpecifyControl?.clearValidators();
        othersSpecifyControl?.setValue(''); // Reset text when unchecked
      }
      othersSpecifyControl?.updateValueAndValidity();
    });
  }

  private setupPrivacyPolicyValidation(): void {
    const privacyAttachmentControl = this.formGroup.get('privacyPolicyAttachment');
    this.formGroup.get('privacyPolicyStatus')?.valueChanges.subscribe((value: string | null) => {
      if (this.readonly()) return;
      if (value === 'notAvailable') {
        privacyAttachmentControl?.clearValidators();
      } else {
        privacyAttachmentControl?.setValidators([Validators.required]);
      }
      privacyAttachmentControl?.updateValueAndValidity();
    });
  }

  private setupNationalityAddressProofValidation(): void {
    const addressProofControl = this.formGroup.get('addressProofAttachment');
    this.formGroup.get('nationality')?.valueChanges.subscribe((value: string) => {
      if (this.readonly()) return;
      if (value === 'others') {
        addressProofControl?.setValidators([Validators.required]);
      } else {
        addressProofControl?.clearValidators();
      }
      addressProofControl?.updateValueAndValidity();
    });
  }

  public isAddressProofRequired(): boolean {
    return this.formGroup.get('nationality')?.value === 'others';
  }

  public isPrivacyPolicyAttachmentRequired(): boolean {
    return this.formGroup.get('privacyPolicyStatus')?.value !== 'notAvailable';
  }


  sectionCLeadGenTouched = false;
  sectionCConsentTouched = false;

  public isLeadGenGroupValid(): boolean {
    const v = this.formGroup.value;
    return v.leadGenExistingClientbase === true || v.leadGenReferrals === true ||
      v.leadGenDigitalMarketing === true || v.leadGenSocialMedia === true ||
      v.leadGenEventsSeminars === true || v.leadGenOthers === true;
  }

  public isWebsiteOrSocialValid(): boolean {
    return this.isFieldFilled(this.formGroup.get('companyWebsite')?.value) ||
      this.isFieldFilled(this.formGroup.get('socialMediaLinks')?.value);
  }

  public isConsentGroupValid(): boolean {
    const v = this.formGroup.value;
    return v.consentSharingData === true || v.consentContact === true;
  }

  async ngOnInit(): Promise<void> {
    this.setSprinner.set(true);
    this.userRole = String(this.sessionStorageService.getCurrentUserRole());

    const dropdownPromise = this.getDropdownOptions();
    let detailsPromise: Promise<void>;
    const isOtherRole = this.userRole !== 'Introducer - Corporate';

    if (!isOtherRole) {
      this.applicationId = Number(this.sessionStorageService.getCurrentUserId());
      detailsPromise = this.loadIntroducerApplicationDetails();
    } else {
      this.applicationId = this.data?.id;

      if (!this.applicationId || isNaN(this.applicationId)) {
        console.warn('Missing/invalid applicationId for role', this.userRole, 'history.state:', this.data);
        this.snackBarService.openFailureSnackBar(
          'Unable to load this application. Please reopen it from the list.'
        );
        this.detailsLoaded = true;
        return;   // don't proceed with a broken id
      }

      detailsPromise = this.loadIntroducerApplicationDetails();
    }

    await Promise.all([dropdownPromise, detailsPromise]);
    this.refreshAllSectionStatuses();

    if (isOtherRole || this.isManagerReview) {
      this.readonly.set(true);
      this.formGroup.disable({ emitEvent: false });
    }

    this.detailsLoaded = true;
  }

  ngAfterViewInit(): void { }

  async getDropdownOptions(): Promise<void> {
    const responseData = await this.newRepApplicationService
      .getDropdownOptions()
      .then((response) => JSON.parse(response))
      .catch((error) => error);

    if (responseData?.status === HttpStatusCode.Ok) {
      this.nationalities = responseData.data.responsedata.constants.nationality_list;
      this.countries = responseData.data.responsedata.constants.country_list;
    }
  }

  async loadIntroducerApplicationDetails(): Promise<void> {
    if (!this.applicationId || isNaN(this.applicationId)) {
      console.warn('Skipping load — invalid applicationId:', this.applicationId);
      this.snackBarService.openFailureSnackBar('Unable to load this application. Please reopen it from the list.');
      return;
    }

    const rawResponse = await this.introducerFormCorporateService
      .getIntroducerApplicationDetails(this.applicationId)
      .catch((error) => {
        console.error('Failed to load introducer corporate application details', error);
        return null;
      });

    if (!rawResponse) {
      return;
    }



    let responseBody: any = rawResponse;
    if (typeof rawResponse === 'string') {
      try {
        responseBody = JSON.parse(rawResponse);
      } catch (e) {
        console.error('Failed to parse introducer corporate application details response', e);
        return;
      }
    }
    this.setSprinner.set(false);
    const body = responseBody?.data ?? responseBody;
    const data = body?.responsedata?.corporate_data;

    if (!data) {
      console.warn('No corporate_data found in response:', body);
      return;
    }

    this.corNationality = data.nationality ?? '';

    if (!['Candidate Submission', 'Candidate Update (Due Diligence)', 'Candidate Update (Assessment)'].includes(data.status)) {
      this.readonly.set(true);
      this.isManagerReview = true;   // reuse existing flag so downstream disable logic still works
      // this.formGroup.disable({ emitEvent: false });

      // if (this.userRole === 'Introducer - Corporate') {
      //   this.showSubmittedAndLogout();
      // }
    } else {
      // Still in "Candidate Submission" → editable ONLY for the applicant themselves
      if (this.userRole === 'Introducer - Corporate') {
        this.readonly.set(false);
      } else {
        this.readonly.set(true);
        // this.formGroup.disable({ emitEvent: false });
      }
    }

    this.patchIfPristine({
      companyName: data.companyName ?? '',
      companyRegistrationNumber: data.companyRegistration ?? '',
      companyRegisteredAddress: data.corCompanyAddress ?? '',
      companyIncorporation: data.countryOfIncorporation ?? '',
      natureOfBusiness: data.corNatureOfBusiness ?? '',
      contactNumber: data.corContactNumber ?? '',

      nricName: data.fullNameNric ?? '',
      passportNo: data.nricNo ?? '',
      designation: data.designation ?? '',
      emailAddress: data.personalEmailAddress ?? '',
      contactNo: data.mobileNumber ?? '',
      nationality: data.nationality ?? '',

      payee: !this.isPlaceholderBankValue(data.nameOfPayee) ? data.nameOfPayee : '',
      bankName: !this.isPlaceholderBankValue(data.bankname) ? data.bankname : '',
      branchCode: !this.isPlaceholderBankValue(data.bankBranchName) ? data.bankBranchName : '',
      acNo: !this.isPlaceholderBankValue(data.bankAccountNumber) ? data.bankAccountNumber : '',

      additionalInfo: data.declarationComment ?? '',
      declarationaccepted: data.isAgreed === true,
      // Only populate if agreed, otherwise keep completely empty:
      declarationName: data.isAgreed === true ? (data.declarationFullName || data.fullNameNric || '') : '',
      date: data.isAgreed === true && data.declarationDate
        ? this.toDateInputValue(data.declarationDate)
        : '' // <-- Keeps it empty initially
    });

    this.viewIntroducerDetails = body.responsedata.attachments ?? [];
    this.patchAttachments(this.viewIntroducerDetails);
    this.buildOtherAttachments();

    this.loadedChecklist = body.responsedata.checklist ?? [];
    this.patchChecklist(this.loadedChecklist);

    this.patchBeneficialOwners(body.responsedata.beneficial ?? []);
    this.patchBeneficialOwnerAttachments(this.viewIntroducerDetails);

    this.patchSectionC(body.responsedata.section_c);
    this.syncSectionCDocumentIds();

    this.recomputeSectionCompletionFromServerData();
  }


  private isPlaceholderBankValue(value: string | undefined | null): boolean {
    if (!value) return true;
    const normalized = value.trim().toUpperCase();
    return normalized === 'PENDING' || normalized === '0' || /^0+$/.test(normalized);
  }

  private patchSectionC(sectionC: any): void {
    if (!sectionC) {
      return;
    }

    this.sectionCId = sectionC.id ?? 0;
    this.leadProcessFlowDocId = sectionC.processFlowDocumentId || null;
    this.privacyPolicyDocId = sectionC.privacyPolicyDocumentId || null;

    this.patchIfPristine({
      leadGenExistingClientbase: sectionC.leadSourceExistingClient ?? false,
      leadGenReferrals: sectionC.leadSourceReferrals ?? false,
      leadGenDigitalMarketing: sectionC.leadSourceDigitalMarketing ?? false,
      leadGenSocialMedia: sectionC.leadSourceSocialMedia ?? false,
      leadGenSocialMediaPlatform: sectionC.leadSourceSocialMediaSpecify ?? '',
      leadGenEventsSeminars: sectionC.leadSourceEventsSeminars ?? false,
      leadGenOthers: sectionC.leadSourceOthers ?? false,
      leadGenOthersSpecify: sectionC.leadSourceOthersSpecify ?? '',
      companyWebsite: sectionC.website ?? '',
      socialMediaLinks: sectionC.socialMediaLinks ?? '',
      businessActivitiesLeadGen: sectionC.businessActivitiesJustification ?? '',
      typicalClients: sectionC.targetAudience ?? '',
      leadGenCampaigns: sectionC.campaignDetails ?? '',
      leadProcessGenerated: sectionC.leadFlowGenerated ?? '',
      leadProcessCollected: sectionC.leadFlowCollected ?? '',
      leadProcessPassedToPias: sectionC.leadFlowPassed ?? '',
      clientInformedMethod: sectionC.clientNotificationMethod ?? '',
      consentSharingData: sectionC.consentShareData ?? false,
      consentContact: sectionC.consentContactRepresentatives ?? false,
      privacyPolicyStatus: sectionC.privacyPolicyStatus || null
    });
  }

  private syncSectionCDocumentIds(): void {
    const processFlowItem = this.getLatestAttachmentByType('Process Flow Diagram');
    if (processFlowItem?.id) {
      this.leadProcessFlowDocId = processFlowItem.id;
    }

    const privacyPolicyItem = this.getLatestAttachmentByType('Privacy Policy / Personal Data Policy');
    if (privacyPolicyItem?.id) {
      this.privacyPolicyDocId = privacyPolicyItem.id;
    }
  }

  private async refreshChecklistIds(): Promise<void> {
    const rawResponse = await this.introducerFormCorporateService
      .getIntroducerApplicationDetails(this.applicationId)
      .catch((error) => {
        console.error('Failed to refresh checklist ids', error);
        return null;
      });

    if (!rawResponse) {
      return;
    }

    let responseBody: any = rawResponse;
    if (typeof rawResponse === 'string') {
      try {
        responseBody = JSON.parse(rawResponse);
      } catch (e) {
        console.error('Failed to parse checklist-refresh response', e);
        return;
      }
    }

    const body = responseBody?.data ?? responseBody;
    this.loadedChecklist = body?.responsedata?.checklist ?? this.loadedChecklist;
  }

  private async refreshSectionCId(): Promise<void> {
    const rawResponse = await this.introducerFormCorporateService
      .getIntroducerApplicationDetails(this.applicationId)
      .catch((error) => {
        console.error('Failed to refresh section C id', error);
        return null;
      });

    if (!rawResponse) {
      return;
    }

    let responseBody: any = rawResponse;
    if (typeof rawResponse === 'string') {
      try {
        responseBody = JSON.parse(rawResponse);
      } catch (e) {
        console.error('Failed to parse section-C-refresh response', e);
        return;
      }
    }

    const body = responseBody?.data ?? responseBody;
    const sectionC = body?.responsedata?.section_c;
    if (sectionC?.id) {
      this.sectionCId = sectionC.id;
    }
  }

  private async refreshAttachments(): Promise<void> {
    const rawResponse = await this.introducerFormCorporateService
      .getIntroducerApplicationDetails(this.applicationId)
      .catch((error) => {
        console.error('Failed to refresh attachments', error);
        return null;
      });

    if (!rawResponse) {
      return;
    }

    let responseBody: any = rawResponse;
    if (typeof rawResponse === 'string') {
      try {
        responseBody = JSON.parse(rawResponse);
      } catch (e) {
        console.error('Failed to parse attachments-refresh response', e);
        return;
      }
    }

    const body = responseBody?.data ?? responseBody;
    this.viewIntroducerDetails = body?.responsedata?.attachments ?? this.viewIntroducerDetails;
    this.syncSectionCDocumentIds();
    this.patchBeneficialOwnerAttachments(this.viewIntroducerDetails);
    // Picks up the ids for a just-uploaded "Other Attachment" so removing it
    // in the same session has real ids to send to deletedocument().
    this.buildOtherAttachments();
  }

  private recomputeSectionCompletionFromServerData(): void {
    Object.keys(this.sectionFieldsMap).forEach(sectionKey => {
      const fields = this.sectionFieldsMap[sectionKey];
      const anyFilled = fields.some(name => {
        const control = this.formGroup.get(name);
        return this.isFieldEngaged(name, control);
      });

      if (anyFilled && this.isSectionValid(sectionKey)) {
        this.sectionSavedState[sectionKey] = true;
      }
    });

    this.sectionSavedState['declaration'] = this.isDeclarationAccepted();
  }

  private patchIfPristine(patch: { [key: string]: any }): void {
    const safePatch: any = {};
    Object.keys(patch).forEach(key => {
      const control = this.formGroup.get(key);
      if (control && control.pristine) {
        safePatch[key] = patch[key];
      }
    });
    this.formGroup.patchValue(safePatch);
  }

  private isZeroDate(value: string | undefined | null): boolean {
    return !value || value.startsWith('0001-01-01');
  }

  private toDateInputValue(isoDate: string): string {
    return new Date(isoDate).toISOString().split('T')[0];
  }

  private patchChecklist(checklist: any[]): void {
    if (!Array.isArray(checklist) || checklist.length === 0) {
      return;
    }

    const patch: { [key: string]: any } = {};

    checklist.forEach(item => {
      const apiCategoryName = item?.categoryName;
      if (!apiCategoryName) return;

      const subQuestionIndex = this.REVERSE_FITPROPER_SUBQUESTION_CATEGORY_MAP[apiCategoryName];
      if (subQuestionIndex) {
        const op1ControlName = `introducerQuestionnairesop1_${subQuestionIndex}`;
        const op1Control = this.formGroup.get(op1ControlName);
        if ((item.isCompleted === true || item.isCompleted === false) && op1Control?.pristine) {
          patch[op1ControlName] = item.isCompleted;
        }
        return;
      }

      const formControlName = this.REVERSE_CHECKLIST_CATEGORY_MAP[apiCategoryName] ?? apiCategoryName;
      const control = this.formGroup.get(formControlName);
      if (control && control.pristine) {
        if (item.isCompleted === true || item.isCompleted === false) {
          patch[formControlName] = item.isCompleted;
        }
      }

      const match = formControlName.match(/^introducerQuestionnaires_(\d+)$/);
      if (match) {
        const index = match[1];
        const followUpControlName = `introducerQuestionnairesyes_${index}`;
        const followUpControl = this.formGroup.get(followUpControlName);
        if (followUpControl && followUpControl.pristine && item.responseValue) {
          patch[followUpControlName] = item.responseValue;
        }
      }
    });

    this.formGroup.patchValue(patch);
  }

  private patchBeneficialOwners(beneficial: any[]): void {
    if (!Array.isArray(beneficial) || beneficial.length === 0) {
      return;
    }

    const sorted = [...beneficial].sort((a, b) => (a?.id ?? 0) - (b?.id ?? 0));

    sorted.forEach((owner, index) => {
      const row = (this.introducers.length > index)
        ? (this.introducers.at(index) as FormGroup)
        : (() => {
          const newRow = this.createIntroducerRow();
          this.introducers.push(newRow);
          return newRow;
        })();

      row.patchValue({
        introducer_Questionnairesfile_8name: owner.comnpanyName ?? '',
        introducer_Questionnairesfile_8idNo: owner.identificationNo ? String(owner.identificationNo) : '',
        introducer_Questionnairesfile_8nationality: owner.nationality ?? '',
        introducer_Questionnairesfile_8residance: owner.countryOfResidence ?? '',
        introducer_Questionnairesfile_8designation: owner.designation ?? '',
        introducer_Questionnairesfile_8tax: owner.countryOfTaxResidence ?? '',
        uploadedFileName: row.get('uploadedFileName')?.value ?? ''
      }, { emitEvent: false });

      if (!row.get('backendId')) {
        (row as any).addControl?.('backendId', this.fb.control(owner.id ?? null));
      } else {
        row.get('backendId')?.setValue(owner.id ?? null, { emitEvent: false });
      }
      row.markAsPristine();
    });
  }

  private clientKeyCounter = 0;

  private generateClientKey(): string {
    const key = `t_${String(this.clientKeyCounter).padStart(4, '0')}`;
    this.clientKeyCounter++;
    return key;
  }

  public isIntroducerFileError(row: AbstractControl): boolean {
    const group = row as FormGroup;
    const fileNameControl = group.get('uploadedFileName');
    const required = this.isIntroducerFileRequired(group);
    const touched = !!(fileNameControl?.touched || fileNameControl?.dirty);
    return required && touched;
  }

  createIntroducerRow(): FormGroup {
    const row = this.fb.group({
      introducer_Questionnairesfile_8name: ['', [Validators.required, noWhitespace]],
      introducer_Questionnairesfile_8idNo: ['', [Validators.required, noWhitespace]],
      introducer_Questionnairesfile_8nationality: ['', Validators.required],
      introducer_Questionnairesfile_8residance: ['', Validators.required],
      introducer_Questionnairesfile_8designation: ['', [Validators.required, noWhitespace]],
      introducer_Questionnairesfile_8tax: ['', Validators.required],
      backendId: [null],
      clientKey: [this.generateClientKey()],
      uploadedFile: [null],
      uploadedFileName: [''],
      uploadedFileId: [null],
      uploadedFileDocTypeId: [null],
      uploadedFileOnboardingId: [null]
    });

    row.valueChanges.subscribe(() => this.syncIntroducerRowValidators(row));
    return row;
  }

  get introducers(): FormArray {
    return this.formGroup.get('introducers') as FormArray;
  }

  addIntroducerRow(): void {
    this.introducers.push(this.createIntroducerRow());
  }

  removeIntroducerRow(index: number): void {
    if (this.introducers.length <= 1) {
      this.snackBarService.openFailureSnackBar('At least one Beneficial Owner is required.');
      return;
    }

    const row = this.introducers.at(index) as FormGroup;
    const backendId = row?.get('backendId')?.value;
    const fileId = row?.get('uploadedFileId')?.value;

    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to remove the Beneficial Owner?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result?.data !== true) {
        return;
      }

      if (fileId) {
        const fileDeleteResponse = await this.newRepApplicationService
          .deletedocument({
            onboardingId: row.get('uploadedFileOnboardingId')?.value,
            docTypeId: row.get('uploadedFileDocTypeId')?.value,
            id: fileId
          })
          .then((response) => JSON.parse(response))
          .catch((error) => {
            console.error('Failed to delete beneficial owner document', error);
            return null;
          });

        if (fileDeleteResponse?.status !== HttpStatusCode.Ok) {
          this.snackBarService.openFailureSnackBar('We couldn\'t remove the attached document. Please try again');
          return;
        }
      }

      if (backendId) {
        const responseData = await this.introducerFormCorporateService
          .deleteBeneficialOwner({ id: backendId, onboardingId: this.applicationId })
          .then((response) => JSON.parse(response))
          .catch((error) => {
            console.error('Failed to delete beneficial owner', error);
            return null;
          });

        if (responseData?.status !== HttpStatusCode.Ok) {
          this.snackBarService.openFailureSnackBar('We couldn\'t remove the Beneficial Owner. Please try again');
          return;
        }
      }

      this.introducers.removeAt(index);
      this.cdr.markForCheck();
    });
  }


  private getLatestBeneficialOwnerAttachment(linkKey: string | number): any | undefined {
    if (!Array.isArray(this.viewIntroducerDetails)) {
      return undefined;
    }
    const categoryName = 'NRIC/Passport of the Beneficial Owner(s)';
    return this.viewIntroducerDetails
      .filter(item =>
        item?.onboardingDocumentType === categoryName &&
        String(item?.responseValue ?? item?.ResponseValue ?? '') === String(linkKey)
      )
      .reduce((latest: any, item: any) => {
        if (!latest) return item;
        return new Date(item.uploadDate) > new Date(latest.uploadDate) ? item : latest;
      }, undefined);
  }

  async onFileSelected(event: Event, index: number): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const row = this.introducers.at(index) as FormGroup;
    if (!row) return;

    if (!this.ALLOWED_FILE_TYPES.has(file.type)) {
      this.snackBarService.openFailureSnackBar('Unsupported file type. Please upload a PDF, JPEG, or PNG.');
      input.value = '';
      return;
    }
    if (file.size > this.MAX_FILE_SIZE) {
      this.snackBarService.openFailureSnackBar('File is too large. Maximum size is 5MB.');
      input.value = '';
      return;
    }

    const linkKey = row.get('backendId')?.value ?? row.get('clientKey')?.value;

    // Delete this row's previously uploaded file first, so re-selecting a
    // file doesn't leave an orphaned duplicate in the DB — same pattern as
    // uploaddocument() for the regular attachment slots.
    const existing = this.getLatestBeneficialOwnerAttachment(linkKey);
    if (existing) {
      const { onboardingId, docTypeId, id } = existing;
      const deleteResponse = await this.newRepApplicationService
        .deletedocument({ onboardingId, docTypeId, id })
        .then((response) => JSON.parse(response))
        .catch((error) => {
          console.error('Failed to delete previous beneficial owner document before re-upload', error);
          return null;
        });

      if (deleteResponse?.status !== HttpStatusCode.Ok) {
        this.snackBarService.openFailureSnackBar('We couldn\'t replace the attachment. Please try again');
        input.value = '';
        return;
      }

      this.viewIntroducerDetails = this.viewIntroducerDetails.filter(item => item !== existing);
    }

    row.patchValue({ uploadedFile: file, uploadedFileName: file.name });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('Description', 'NRIC/Passport of the Beneficial Owner(s)');
    formData.append('filename', file.name);
    formData.append('ResponseValue', String(linkKey));

    const responseData = await this.newRepApplicationService
      .uploadDocuments(formData)
      .then((response) => JSON.parse(response))
      .catch((error) => error);

    if (responseData?.status === HttpStatusCode.Ok) {
      row.patchValue({ uploadedFileName: responseData.data.responsedata });
      await this.refreshAttachments();
      this.snackBarService.openSuccessSnackBar('document uploaded successfully!');
    } else if (responseData?.code === HttpStatusCode.BadRequest) {
      this.snackBarService.openFailureSnackBar('We couldn\u2019t process your request. Please try again');
      input.value = '';
    }
  }

  isPanelExpanded(index: number): boolean {
    return this.activePanelIndex === index;
  }

  onPanelOpened(index: number): void {
    this.activePanelIndex = index;

    // if (index === this.sectionOrder.length - 1) {
    //   const dateControl = this.formGroup.get('date');
    //   if (!dateControl?.value) {
    //     dateControl?.setValue(this.toDateInputValue(new Date().toISOString()));
    //   }
    // }
  }

  onPanelClosed(index: number): void {
    if (this.activePanelIndex === index) {
      this.activePanelIndex = -1;
    }
  }

  private isFieldFilled(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return true;   // true or false both count as "filled" for tri-state (null/true/false) fields
  }

  private readonly DEFAULT_FALSE_CHECKBOX_FIELDS = new Set([
    'leadGenExistingClientbase', 'leadGenReferrals', 'leadGenDigitalMarketing',
    'leadGenSocialMedia', 'leadGenEventsSeminars', 'leadGenOthers',
    'consentSharingData', 'consentContact'
  ]);

  private isFieldEngaged(name: string, control: AbstractControl | null): boolean {
    if (!control) return false;
    if (this.DEFAULT_FALSE_CHECKBOX_FIELDS.has(name)) {
      // These start at false with no way to distinguish "untouched" from
      // "unchecked" by value alone - so use dirty/touched instead, and still
      // count true as filled regardless of dirty state.
      return control.value === true || control.dirty || control.touched;
    }
    return this.isFieldFilled(control.value);
  }

  changeFullName(fullName: string): string {
    return fullName
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private isSectionValid(sectionKey: string): boolean {
    const fields = this.sectionFieldsMap[sectionKey] || [];

    if (sectionKey === 'additional') {
      return true;
    }

    if (sectionKey === 'sectionC') {
      const leadProcessFlowValid = this.formGroup.get('leadProcessFlowAttachment')?.valid ?? true;
      const privacyPolicyValid = this.formGroup.get('privacyPolicyAttachment')?.valid ?? true;
      const businessActivitiesValid = this.formGroup.get('businessActivitiesLeadGen')?.valid ?? true;
      const typicalClientsValid = this.formGroup.get('typicalClients')?.valid ?? true;
      const campaignsValid = this.formGroup.get('leadGenCampaigns')?.valid ?? true;
      const processFlowTextValid =
        (this.formGroup.get('leadProcessGenerated')?.valid ?? true) &&
        (this.formGroup.get('leadProcessCollected')?.valid ?? true) &&
        (this.formGroup.get('leadProcessPassedToPias')?.valid ?? true);
      const clientInformedValid = this.formGroup.get('clientInformedMethod')?.valid ?? true;

      // NEW: conditional "specify" fields — only matter when their checkbox is ticked,
      // but .valid already handles that since the validator is added/cleared based on the checkbox.
      const socialMediaSpecifyValid = this.formGroup.get('leadGenSocialMediaPlatform')?.valid ?? true;
      const othersSpecifyValid = this.formGroup.get('leadGenOthersSpecify')?.valid ?? true;

      return leadProcessFlowValid && privacyPolicyValid &&
        this.isLeadGenGroupValid() && this.isWebsiteOrSocialValid() &&
        businessActivitiesValid && typicalClientsValid && campaignsValid &&
        processFlowTextValid && clientInformedValid && this.isConsentGroupValid() &&
        socialMediaSpecifyValid && othersSpecifyValid;
    }

    if (sectionKey === 'general') {
      const conditionalMap: { [key: string]: string } = {
        introducerQuestionnaires_1: 'introducerQuestionnairesyes_1',
        introducerQuestionnaires_3: 'introducerQuestionnairesyes_3',
        introducerQuestionnaires_5: 'introducerQuestionnairesyes_5',
        introducerQuestionnaires_6: 'introducerQuestionnairesyes_6'
      };

      const questionsValid = fields.every(name => {
        const control = this.formGroup.get(name);
        if (!control) return true;

        const parentEntry = Object.entries(conditionalMap).find(([, followUp]) => followUp === name);
        if (parentEntry) {
          const parentControl = this.formGroup.get(parentEntry[0]);
          if (parentControl?.value !== true) return true;
          return control.valid;
        }
        return control.valid;
      });

      return questionsValid && this.areIntroducersValid();
    }

    if (sectionKey === 'fitproper') {
      return fields.every(name => {
        const control = this.formGroup.get(name);
        if (!control) return true;

        if (name === 'introducerQuestionnairesyes_8') {
          return this.formGroup.get('introducerQuestionnaires_8')?.value !== true || control.valid;
        }
        if (name === 'introducerQuestionnairesop1_8') {
          return this.formGroup.get('introducerQuestionnaires_8')?.value !== true || this.isFieldFilled(control.value);
        }
        return control.valid;
      });
    }

    return fields.every(name => {
      const control = this.formGroup.get(name);
      return !control || control.valid;
    });
  }

  private readonly INTRODUCER_ROW_FIELDS = [
    'introducer_Questionnairesfile_8name',
    'introducer_Questionnairesfile_8idNo',
    'introducer_Questionnairesfile_8nationality',
    'introducer_Questionnairesfile_8residance',
    'introducer_Questionnairesfile_8designation',
    'introducer_Questionnairesfile_8tax'
  ];

  private isIntroducerRowEmpty(row: FormGroup): boolean {
    const anyFieldFilled = this.INTRODUCER_ROW_FIELDS.some(f => this.isFieldFilled(row.get(f)?.value));
    const hasFile = this.isFieldFilled(row.get('uploadedFileName')?.value);
    return !anyFieldFilled && !hasFile;
  }

  private isIntroducerRowComplete(row: FormGroup): boolean {
    if (this.isIntroducerRowEmpty(row)) {
      return true;
    }
    const allFieldsFilled = this.INTRODUCER_ROW_FIELDS.every(f => this.isFieldFilled(row.get(f)?.value));
    const hasFile = this.isFieldFilled(row.get('uploadedFileName')?.value);
    return allFieldsFilled && hasFile;
  }

  public areIntroducersValid(): boolean {
    return this.introducers.controls.every(row => this.isIntroducerRowComplete(row as FormGroup));
  }

  public isIntroducerFileRequired(row: AbstractControl): boolean {
    const group = row as FormGroup;
    return !this.isIntroducerRowEmpty(group) && !this.isFieldFilled(group.get('uploadedFileName')?.value);
  }

  private computeSectionStatus(sectionKey: string): 'completed' | 'inprogress' | 'notstarted' {


    if (this.isManagerReview) {
      return 'completed';
    }

    if (sectionKey === 'declaration') {
      if (this.sectionSavedState['declaration'] && this.isDeclarationAccepted()) {
        return 'completed';
      }
      if (this.isDeclarationAccepted()) {
        return 'inprogress';
      }
      return 'notstarted';
    }

    if (sectionKey === 'additional') {
      if (this.readonly() && this.isDeclarationAccepted()) {
        return 'completed';
      }
      if (this.sectionSavedState['additional']) {
        return 'completed';
      }
      const control = this.formGroup.get('additionalInfo');
      const isFilled = this.isFieldFilled(control?.value);
      if (isFilled || control?.dirty || control?.touched) {
        return 'inprogress';
      }
      return 'notstarted';
    }

    const fields = this.sectionFieldsMap[sectionKey] || [];
    const anyFilled = fields.some(name => {
      const control = this.formGroup.get(name);
      return this.isFieldEngaged(name, control);
    }) || (sectionKey === 'general' && this.isAnyIntroducerRowEngaged());

    if (!anyFilled) {
      return 'notstarted';
    }

    return (this.sectionSavedState[sectionKey] && this.isSectionValid(sectionKey))
      ? 'completed'
      : 'inprogress';
  }

  private refreshAllSectionStatuses(): void {
    const next: Record<string, 'completed' | 'inprogress' | 'notstarted'> = {} as any;
    Object.keys(this.sectionFieldsMap).forEach(key => {
      next[key] = this.computeSectionStatus(key);
    });
    this.sectionStatuses.set(next);
    this.cdr.detectChanges();
  }

  getSectionStatus(sectionKey: string): 'completed' | 'inprogress' | 'notstarted' {
    return this.sectionStatuses()[sectionKey] ?? 'notstarted';
  }

  allSectionsCompletedExceptDeclaration(): boolean {
    const statuses = this.sectionStatuses();
    return Object.keys(this.sectionFieldsMap)
      .filter(key => key !== 'declaration')
      .every(key => statuses[key] === 'completed');
  }


  private readonly INTRODUCER_ROW_TEXT_FIELDS = new Set([
    'introducer_Questionnairesfile_8name',
    'introducer_Questionnairesfile_8idNo',
    'introducer_Questionnairesfile_8designation'
  ]);

  private syncIntroducerRowValidators(row: FormGroup): void {
    const shouldRequire = !this.isIntroducerRowEmpty(row);
    this.INTRODUCER_ROW_FIELDS.forEach(f => {
      const control = row.get(f);
      if (!control) return;
      if (shouldRequire) {
        const isTextField = this.INTRODUCER_ROW_TEXT_FIELDS.has(f);
        control.setValidators(isTextField ? [Validators.required, noWhitespace] : [Validators.required]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }
  private buildUpdatePayload(sectionKey: string): any {
    const v = this.formGroup.value;
    const payload: any = { id: this.applicationId };

    switch (sectionKey) {
      case 'introducer':
        payload.personal_detail = {
          company_name:  this.changeFullName(v.companyName),
          company_registration_number: v.companyRegistrationNumber,
          company_registered_address: v.companyRegisteredAddress,
          country_of_incorporation: v.companyIncorporation,
          nature_of_business: v.natureOfBusiness,
          cor_contact_number: v.contactNumber
        };
        break;

      case 'liaison':
        payload.authorised_liaison_personnel = {
          full_name_nric:  this.changeFullName(v.nricName),
          nric_no: v.passportNo,
          designation: v.designation,
          email_address: v.emailAddress,
          contact_number: v.contactNo,
          cor_nationality: v.nationality
        };
        break;

      case 'payment':
        payload.bank_detail = {
          bankname: v.bankName,
          bank_account_number: v.acNo,
          bank_branch_name: v.branchCode,
          name_of_payee: v.payee
        };
        break;

      case 'general':
        payload.checklist = this.buildChecklistPayload('general');
        payload.Introducer_corporate_beneficial = this.buildBeneficialOwnersPayload();
        break;

      case 'fitproper':
        payload.checklist = this.buildChecklistPayload('fitproper');
        break;

      case 'sectionC':
        payload.corporate_Section_c = {
          id: this.sectionCId,
          introducerCorporate_id: this.applicationId,
          lead_sourcexisting_client: v.leadGenExistingClientbase,
          lead_source_referrals: v.leadGenReferrals,
          lead_source_digital_marketing: v.leadGenDigitalMarketing,
          lead_source_socialmedia: v.leadGenSocialMedia,
          lead_source_social_media_specify: v.leadGenSocialMediaPlatform,
          lead_source_eventsseminars: v.leadGenEventsSeminars,
          lead_source_others: v.leadGenOthers,
          leadsource_others_specify: v.leadGenOthersSpecify,
          website: v.companyWebsite,
          social_medialinks: v.socialMediaLinks,
          business_activitiesjustification: v.businessActivitiesLeadGen,
          target_audience: v.typicalClients,
          campaign_details: v.leadGenCampaigns,
          leadflow_generated: v.leadProcessGenerated,
          leadflowcollected: v.leadProcessCollected,
          leadflowpassed: v.leadProcessPassedToPias,
          processflowdocument_id: this.leadProcessFlowDocId ?? 0,
          clientnotification_method: v.clientInformedMethod,
          consentshare_data: v.consentSharingData,
          consentcontact_representatives: v.consentContact,
          privacypolicy_status: v.privacyPolicyStatus ?? '',
          privacypolicydocument_id: this.privacyPolicyDocId ?? 0
        };
        break;

      case 'additional':
        payload.declaration_comment_detail = {
          declaration_comment: v.additionalInfo
        };
        break;

      case 'declaration':
        payload.declaration_full_detail = {
          isAgreed: v.declarationaccepted === true,
          declaration_full_name: v.declarationName,

          declaration_date: this.formatDateOrNull(v.date)
        };
        break;
    }

    return payload;
  }

  private formatDateOrNull(value: any): string | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
  }

  private getChecklistItemId(categoryName: string): number {
    const match = this.loadedChecklist.find(item => item?.categoryName === categoryName);
    return match?.id ?? 0;
  }

  private buildBeneficialOwnersPayload(): any[] {
    return this.introducers.controls.map((row) => {
      const v = row.value;
      return {
        id: row.get('backendId')?.value ?? 0,
        onboardingId: this.applicationId,
        comnpany_name: v.introducer_Questionnairesfile_8name,
        identification_no: String(v.introducer_Questionnairesfile_8idNo) || '',
        nationality: v.introducer_Questionnairesfile_8nationality,
        country_of_residence: v.introducer_Questionnairesfile_8residance,
        designation: v.introducer_Questionnairesfile_8designation,
        country_of_tax_residence: v.introducer_Questionnairesfile_8tax
      };
    });
  }

  private buildChecklistPayload(sectionKey: 'general' | 'fitproper'): any[] {
    const checklist: any[] = [];
    const v = this.formGroup.value;

    const questionNumbers = sectionKey === 'general'
      ? [1, 2, 3, 4, 5, 6, 7, 9]
      : [8, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23,
        24, 25, 26, 27, 28, 29, 30, 31, 32];

    questionNumbers.forEach(i => {
      const key = `introducerQuestionnaires_${i}`;
      if (v[key] === undefined) return;

      const categoryName = this.CHECKLIST_CATEGORY_MAP[key];
      if (!categoryName) return;

      checklist.push({
        id: this.getChecklistItemId(categoryName),
        category_name: categoryName,
        is_completed: v[key],
        response_value: v[`introducerQuestionnairesyes_${i}`] ?? ''
      });

      const subCategoryName = this.FITPROPER_SUBQUESTION_CATEGORY_MAP[i];
      if (subCategoryName) {
        const op1 = v[`introducerQuestionnairesop1_${i}`];
        checklist.push({
          id: this.getChecklistItemId(subCategoryName),
          category_name: subCategoryName,
          is_completed: op1 === true ? true : (op1 === false ? false : null),
          response_value: ''
        });
      }
    });

    return checklist;
  }

  private async refreshBeneficialOwnerIds(): Promise<void> {
    const rawResponse = await this.introducerFormCorporateService
      .getIntroducerApplicationDetails(this.applicationId)
      .catch((error) => { console.error('Failed to refresh beneficial owner ids', error); return null; });

    if (!rawResponse) return;

    let responseBody: any = rawResponse;
    if (typeof rawResponse === 'string') {
      try { responseBody = JSON.parse(rawResponse); }
      catch (e) { console.error('Failed to parse beneficial-owner-refresh response', e); return; }
    }

    const body = responseBody?.data ?? responseBody;
    const beneficial: any[] = body?.responsedata?.beneficial ?? [];
    if (!Array.isArray(beneficial) || beneficial.length === 0) return;

    const sorted = [...beneficial].sort((a, b) => (a?.id ?? 0) - (b?.id ?? 0));

    for (let index = 0; index < sorted.length; index++) {
      if (this.introducers.length <= index) continue;
      const row = this.introducers.at(index) as FormGroup;
      const owner = sorted[index];
      const hadNoBackendIdBefore = !row.get('backendId')?.value;

      row.get('backendId')?.setValue(owner.id ?? null, { emitEvent: false });

      const fileId = row.get('uploadedFileId')?.value;
      const file = row.get('uploadedFile')?.value as File | null;
      if (hadNoBackendIdBefore && owner?.id && fileId && file) {
        await this.newRepApplicationService
          .deletedocument({
            onboardingId: row.get('uploadedFileOnboardingId')?.value,
            docTypeId: row.get('uploadedFileDocTypeId')?.value,
            id: fileId
          })
          .catch((error) => console.error('Failed to unlink temp beneficial owner document', error));

        const formData = new FormData();
        formData.append('file', file);
        formData.append('Description', 'NRIC/Passport of the Beneficial Owner(s)');
        formData.append('filename', file.name);
        formData.append('ResponseValue', String(owner.id));

        await this.newRepApplicationService
          .uploadDocuments(formData)
          .then((response) => JSON.parse(response))
          .catch((error) => console.error('Failed to re-link beneficial owner document to backend id', error));
      }
    }

    this.viewIntroducerDetails = body?.responsedata?.attachments ?? this.viewIntroducerDetails;
    this.patchBeneficialOwnerAttachments(this.viewIntroducerDetails);
  }

  private async saveSectionToApi(sectionKey: string, silent: boolean = false): Promise<void> {
    if (!this.applicationId) {
      console.warn('No application id yet - skipping API update for section:', sectionKey);
      return;
    }
    const payload = this.buildUpdatePayload(sectionKey);

    const responseData = await this.introducerFormCorporateService
      .updateIntroducerApplicationDetails(payload)
      .then((response) => JSON.parse(response))
      .catch((error) => {
        console.error(`Failed to save section "${sectionKey}"`, error);
        return null;
      });

    if (responseData?.status === HttpStatusCode.Ok) {
      if (sectionKey === 'general' || sectionKey === 'fitproper') {
        await this.refreshChecklistIds();
      }
      if (sectionKey === 'general') {
        await this.refreshBeneficialOwnerIds();
      }
      if (sectionKey === 'sectionC') {
        await this.refreshSectionCId();
        await this.refreshAttachments();
      }
      if (!silent) {
        this.snackBarService.openSuccessSnackBar('Saved successfully!');
      }
    } else {
      this.snackBarService.openFailureSnackBar('We couldn\'t process your request. Please try again');
    }
  }

  private patchBeneficialOwnerAttachments(attachments: any[]): void {
    if (!Array.isArray(attachments) || attachments.length === 0) return;

    const categoryName = 'NRIC/Passport of the Beneficial Owner(s)';
    const ownerAttachments = attachments.filter(item => item?.onboardingDocumentType === categoryName);
    if (ownerAttachments.length === 0) return;

    const latestByKey = new Map<string, any>();
    ownerAttachments.forEach(item => {
      const key = String(item?.responseValue ?? item?.ResponseValue ?? '');
      if (!key) return;
      const existing = latestByKey.get(key);
      if (!existing || new Date(item.uploadDate) > new Date(existing.uploadDate)) {
        latestByKey.set(key, item);
      }
    });

    this.introducers.controls.forEach((row) => {
      const backendId = (row as FormGroup).get('backendId')?.value;
      const clientKey = (row as FormGroup).get('clientKey')?.value;
      const item = (backendId && latestByKey.get(String(backendId))) || latestByKey.get(clientKey);
      if (!item) return;

      row.patchValue({
        uploadedFileName: item.fileName,
        uploadedFileId: item.id ?? null,
        uploadedFileDocTypeId: item.docTypeId ?? null,
        uploadedFileOnboardingId: item.onboardingId ?? null
      }, { emitEvent: false });
    });
  }

  public removeBeneficialOwnerFile(index: number): void {
    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to remove the attachement?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result?.data !== true) {
        return;
      }

      const row = this.introducers.at(index) as FormGroup;
      if (!row) return;

      const id = row.get('uploadedFileId')?.value;
      const docTypeId = row.get('uploadedFileDocTypeId')?.value;
      const onboardingId = row.get('uploadedFileOnboardingId')?.value;

      if (id) {
        const responseData = await this.newRepApplicationService
          .deletedocument({ onboardingId, docTypeId, id })
          .then((response) => JSON.parse(response))
          .catch((error) => {
            console.error('Failed to delete beneficial owner document', error);
            return null;
          });

        if (responseData?.status !== HttpStatusCode.Ok) {
          this.snackBarService.openFailureSnackBar('We couldn\'t remove the attachment. Please try again');
          return;
        }
      }

      row.patchValue({
        uploadedFile: null,
        uploadedFileName: '',
        uploadedFileId: null,
        uploadedFileDocTypeId: null,
        uploadedFileOnboardingId: null
      });
      row.markAsDirty();
    });
  }

  async onSectionSave(sectionKey: string, silent: boolean = false): Promise<void> {
    const fields = this.sectionFieldsMap[sectionKey] || [];

    const generalConditionalMap: { [key: string]: string } = {
      introducerQuestionnairesyes_1: 'introducerQuestionnaires_1',
      introducerQuestionnairesyes_3: 'introducerQuestionnaires_3',
      introducerQuestionnairesyes_5: 'introducerQuestionnaires_5',
      introducerQuestionnairesyes_6: 'introducerQuestionnaires_6'
    };

    fields.forEach(name => {
      if (sectionKey === 'general' && generalConditionalMap[name]) {
        const parentControl = this.formGroup.get(generalConditionalMap[name]);
        if (parentControl?.value !== true) return;
      }
      if (sectionKey === 'fitproper' &&
        (name === 'introducerQuestionnairesyes_8' || name === 'introducerQuestionnairesop1_8')) {
        if (this.formGroup.get('introducerQuestionnaires_8')?.value !== true) return;
      }
      this.formGroup.get(name)?.markAsTouched();
    });

    if (sectionKey === 'general') {
      this.introducers.controls.forEach(row => {
        const group = row as FormGroup;
        const fileControl = group.get('uploadedFileName');

        if (this.isIntroducerRowEmpty(group)) {
          fileControl?.setErrors(null);
          return;
        }

        row.markAllAsTouched();

        const hasFile = this.isFieldFilled(fileControl?.value);
        if (!hasFile) {
          fileControl?.setErrors({ required: true });
        } else {
          fileControl?.setErrors(null);
        }
      });
    }

    if (sectionKey === 'sectionC') {
      this.sectionCLeadGenTouched = true;
      this.sectionCConsentTouched = true;
    }

    if (sectionKey === 'additional') {
      this.sectionSavedState['additional'] = true;
    } else {
      this.sectionSavedState[sectionKey] = this.isSectionValid(sectionKey);
    }

    this.refreshAllSectionStatuses();

    await this.saveSectionToApi(sectionKey, silent);

    if (!silent) {
      const currentIndex = this.sectionOrder.indexOf(sectionKey);
      if (currentIndex !== -1 && currentIndex < this.sectionOrder.length - 1) {
        this.activePanelIndex = currentIndex + 1;
      } else {
        this.activePanelIndex = -1;
      }
    }
  }

  async onFinalSubmit(event?: Event): Promise<void> {
    event?.preventDefault();

    if (this.readonly()) {
      return;
    }
    if (!this.isDeclarationAccepted()) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Are you sure you want to submit this Application?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result?.data === true) {
        await this.proceedFinalSubmit();
      }
    });
  }

  private async proceedFinalSubmit(): Promise<void> {
    this.setSprinner.set(true);

    for (const key of this.sectionOrder.filter(k => k !== 'declaration')) {
      await this.onSectionSave(key, true);
    }
    this.introducers.controls.forEach(row => this.syncIntroducerRowValidators(row as FormGroup));

    this.sectionSavedState['declaration'] = true;
    this.refreshAllSectionStatuses();
    await this.saveSectionToApi('declaration', true);

    const allSectionsValid = Object.keys(this.sectionFieldsMap)
      .every(key => this.isSectionValid(key));

    if (!allSectionsValid || this.formGroup.invalid) {
      const invalidTopLevel: string[] = [];
      Object.keys(this.formGroup.controls).forEach(key => {
        const c = this.formGroup.get(key);
        if (c?.invalid) invalidTopLevel.push(key);
      });

      this.formGroup.markAllAsTouched();
      this.introducers.controls.forEach(row => (row as FormGroup).markAllAsTouched());

      const firstInvalidSection = this.sectionOrder.find(key => !this.isSectionValid(key));
      if (firstInvalidSection) {
        this.activePanelIndex = this.sectionOrder.indexOf(firstInvalidSection);
      }

      this.snackBarService.openFailureSnackBar(
        'Some required fields are missing or invalid. Please review the highlighted sections.'
      );

      // console.log('[proceedFinalSubmit] BLOCKED — invalid top-level controls:', invalidTopLevel);

      this.setSprinner.set(false);
      return;
    }

    // Disable FIRST and silently — before flipping statuses — so the
    // disable's valueChanges emissions can't undo what we're about to set.
    // Mark every section completed and compute status FIRST, while the
    // form is still enabled — isSectionValid() needs real control.valid
    // results, which disabled controls can't give.
    this.activePanelIndex = -1;
    Object.keys(this.sectionFieldsMap).forEach(key => {
      this.sectionSavedState[key] = true;
    });
    this.refreshAllSectionStatuses();

    // NOW it's safe to disable — status is already frozen in the signal.
    this.readonly.set(true);
    this.formGroup.disable({ emitEvent: false });

    this.next.emit(this.formGroup.valid);

    this.setSprinner.set(false);
    // this.showSubmittedAndLogout();
  }

  saveAndContinue(): void {
    this.onFinalSubmit();
  }

  onDeclarationChange(checked: boolean): void {
    const control = this.formGroup.get('declarationaccepted');
    control?.setValue(checked);
    control?.markAsDirty();
    control?.markAsTouched();

    if (checked) {
      // Auto-fill the Name field using the Authorised Liaison Personnel's name (nricName)
      this.formGroup.get('declarationName')?.setValue(this.formGroup.get('nricName')?.value ?? '');
      // Auto-fill today's date
      this.formGroup.get('date')?.setValue(this.toDateInputValue(new Date().toISOString()));
    } else {
      this.formGroup.get('declarationName')?.reset();
      this.formGroup.get('date')?.reset();
    }

    this.refreshAllSectionStatuses();
  }

  isDeclarationAccepted(): boolean {
    return this.formGroup.get('declarationaccepted')?.value === true;
  }

  private setupAutoUnsaveOnChange(): void {
    Object.keys(this.sectionFieldsMap).forEach(sectionKey => {
      const fields = this.sectionFieldsMap[sectionKey];
      fields.forEach(fieldName => {
        const control = this.formGroup.get(fieldName);
        control?.valueChanges.subscribe(() => {
          if (!this.detailsLoaded) {
            return;
          }
          if (this.sectionSavedState[sectionKey]) {
            this.sectionSavedState[sectionKey] = false;
          }
          this.refreshAllSectionStatuses();
        });
      });
    });

    this.introducers.valueChanges.subscribe(() => {
      if (!this.detailsLoaded) {
        return;
      }
      if (this.sectionSavedState['general']) {
        this.sectionSavedState['general'] = false;
      }
      this.refreshAllSectionStatuses();
    });
  }

  onCheckboxChange(checked: boolean, value: boolean, field: string): void {
    const control = this.formGroup.get(field);
    control?.setValue(checked ? value : null);
    control?.markAsDirty();
    control?.markAsTouched();
  }

  onCheckboxChange1(checked: boolean, value: boolean, field: string, index: number): void {
    const control = this.formGroup.get(field);
    control?.setValue(checked ? value : null);
    control?.markAsDirty();
    control?.markAsTouched();

    if (checked && value === true) {
      if (index === 22 || index === 10 || index === 27 || index === 28) {
        this.formGroup.get('attachment' + index)?.setValidators([Validators.required]);
        this.formGroup.get('attachment' + index)?.updateValueAndValidity();
      } else if (index === 1 || index === 3 || index === 4 || index === 5 || index === 6 || index === 8) {
        this.formGroup.get('introducerQuestionnairesyes_' + index)?.setValidators([Validators.required, noWhitespace]);
        this.formGroup.get('introducerQuestionnairesyes_' + index)?.updateValueAndValidity();
      }
    } else {
      this.formGroup.get('attachment' + index)?.clearValidators();
      this.formGroup.get('attachment' + index)?.reset();
      this.formGroup.get('attachment' + index)?.updateValueAndValidity();

      this.formGroup.get('introducerQuestionnairesyes_' + index)?.clearValidators();
      this.formGroup.get('introducerQuestionnairesyes_' + index)?.reset();
      this.formGroup.get('introducerQuestionnairesyes_' + index)?.updateValueAndValidity();
    }
  }

  questionError(field: string): boolean {
    const control = this.formGroup.get(field);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  public nricrequired(): boolean {
    return true;
  }

  public onAttachmentFileChange(event: Event, controlName: string, fileVar: string, categoryName: string, index: number): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const control = this.formGroup.get(controlName);

    this.formGroup.patchValue({
      [fileVar]: file ?? null,
      [controlName]: file?.name ?? null
    });

    if (!control) return;

    control.markAsTouched();
    control.markAsDirty();
    control.setErrors(null);

    if (!file) {
      control.setValue(null);
      return;
    }

    if (!this.ALLOWED_FILE_TYPES.has(file.type)) {
      control.setErrors({ invalidType: true });
      return;
    }

    if (file.size > this.MAX_FILE_SIZE) {
      control.setErrors({ maxSize: true });
      return;
    }

    control.updateValueAndValidity();
    this.uploaddocument(fileVar, controlName, categoryName, index);
  }

  async uploaddocument(fileVar: string, controlName: string, categoryName: string, index: number): Promise<void> {
    // Delete the previous file for this slot first, so replacing a document
    // doesn't leave an orphaned duplicate row in the DB (same as individual form).
    const existing = this.getLatestAttachmentByType(categoryName);
    if (existing) {
      const { onboardingId, docTypeId, id } = existing;
      const deleteResponse = await this.newRepApplicationService
        .deletedocument({ onboardingId, docTypeId, id })
        .then((response) => JSON.parse(response))
        .catch((error) => {
          console.error('Failed to delete previous attachment before re-upload', error);
          return null;
        });

      if (deleteResponse?.status !== HttpStatusCode.Ok) {
        this.snackBarService.openFailureSnackBar('We couldn\'t replace the attachment. Please try again');
        return;
      }

      this.viewIntroducerDetails = this.viewIntroducerDetails.filter(item => item !== existing);
    }

    // "Other Attachments" share one document type and are told apart by the
    // row index in ResponseValue; every other field keeps its own document type.
    const isOtherAttachment = this.isOtherAttachmentKey(categoryName);
    const formData = new FormData();
    formData.append('file', this.formGroup.get(fileVar)?.value);
    formData.append('Description', isOtherAttachment ? this.otherAttachmentDescription : categoryName);
    formData.append('filename', this.formGroup.get(fileVar)?.value?.name);
    formData.append('ResponseValue', isOtherAttachment ? this.otherAttachmentIndex(categoryName) : index.toString());

    const responseData = await this.newRepApplicationService
      .uploadDocuments(formData)
      .then((response) => JSON.parse(response))
      .catch((error) => error);

    if (responseData.status === HttpStatusCode.Ok) {
      this.formGroup.patchValue({
        [controlName]: responseData.data.responsedata
      });

      await this.refreshAttachments();
      this.snackBarService.openSuccessSnackBar('document uploaded successfully!');
    } else if (responseData.code === HttpStatusCode.BadRequest) {
      this.snackBarService.openFailureSnackBar('We couldn\u2019t process your request. Please try again');
    }
  }

  /**
   * Most recently uploaded attachment for a document type. "Other Attachments"
   * rows all share one document type, so those are matched on the row index
   * carried in responseValue instead.
   */
  private getLatestAttachmentByType(categoryName: string): any | undefined {
    if (!Array.isArray(this.viewIntroducerDetails)) {
      return undefined;
    }
    const matches = this.isOtherAttachmentKey(categoryName)
      ? this.viewIntroducerDetails.filter(item => this.otherAttachmentIndexOf(item) === this.otherAttachmentRow(categoryName))
      : this.viewIntroducerDetails.filter(item => item?.onboardingDocumentType === categoryName);

    return matches
      .reduce((latest: any, item: any) => {
        if (!latest) return item;
        return new Date(item.uploadDate) > new Date(latest.uploadDate) ? item : latest;
      }, undefined);
  }

  // ---------- Other Attachments ----------

  /**
   * Restores any previously uploaded "Other Attachments" and always keeps at
   * least one empty row. Safe to call repeatedly (after every attachments
   * re-fetch) - restoreOtherAttachment() only adds controls it hasn't seen.
   */
  private buildOtherAttachments(): void {
    this.viewIntroducerDetails?.forEach(item => {
      const index = this.otherAttachmentIndexOf(item);
      if (index !== null) {
        this.restoreOtherAttachment(`${this.otherAttachmentPrefix}${index}`, item.fileName);
      }
    });

    if (this.otherAttachmentKeys.length === 0) {
      this.addOtherAttachment();
    }

    // The API lists the documents in its own order, so the rows are put back in row
    // order here. Left unsorted the fields swap places on every fetch, and because
    // the template hangs the remove button off every row but the first and the add
    // button off the last, those buttons end up on whichever rows came back first.
    this.otherAttachmentKeys.sort((a, b) => this.otherAttachmentRow(a) - this.otherAttachmentRow(b));
  }

  /** True when the category name is one of the "Other Attachments" control keys. */
  private isOtherAttachmentKey(categoryName: string): boolean {
    return categoryName.startsWith(this.otherAttachmentPrefix);
  }

  /** The row index sent as ResponseValue for an "Other Attachments" control key. */
  private otherAttachmentIndex(key: string): string {
    return key.slice(this.otherAttachmentPrefix.length);
  }

  /** Row index of a control key as a number, for matching and ordering. */
  private otherAttachmentRow(key: string): number {
    return Number(this.otherAttachmentIndex(key));
  }

  /**
   * Row index an uploaded document belongs to, or null when it isn't an "Other
   * Attachments" row. Uploads carry 'OtherAttachments' as the document type with
   * the index in responseValue; rows saved before that change kept the index in
   * the document type itself, so both shapes are recognised here. The index is
   * read as a number so a row is still matched when the API echoes it back in a
   * different form (' 2', 2) than the one that was sent.
   */
  private otherAttachmentIndexOf(item: any): number | null {
    const docType: string = item?.onboardingDocumentType ?? '';
    const raw = docType === this.otherAttachmentDescription
      ? String(item?.responseValue ?? '').trim()
      : docType.startsWith(this.otherAttachmentPrefix)
        ? docType.slice(this.otherAttachmentPrefix.length)
        : '';

    const row = Number(raw);
    return raw !== '' && Number.isFinite(row) ? row : null;
  }

  private restoreOtherAttachment(key: string, fileName: string): void {
    if (!this.otherAttachmentKeys.includes(key)) {
      this.formGroup.addControl(key, this.fb.control([null]));
      this.formGroup.addControl(`${key}fileName`, this.fb.control(''));
      this.otherAttachmentKeys.push(key);

      const seq = Number(key.slice(this.otherAttachmentPrefix.length));
      if (!Number.isNaN(seq) && seq > this.otherAttachmentSeq) {
        this.otherAttachmentSeq = seq;
      }
    }
    this.formGroup.get(`${key}fileName`)?.setValue(fileName);
  }

  /** Duplicates the "Other Attachments" field so another file can be attached. */
  addOtherAttachment(): void {
    const key = `${this.otherAttachmentPrefix}${++this.otherAttachmentSeq}`;
    this.formGroup.addControl(key, this.fb.control([null]));
    this.formGroup.addControl(`${key}fileName`, this.fb.control(''));
    this.otherAttachmentKeys.push(key);
  }

  /** Removes a duplicated "Other Attachments" row, deleting its uploaded file first when there is one. */
  removeOtherAttachment(key: string): void {
    if (!this.formGroup.get(`${key}fileName`)?.value) {
      this.dropOtherAttachment(key);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to remove the attachement?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result?.data !== true) {
        return;
      }

      const match = this.getLatestAttachmentByType(key);
      if (match) {
        const { onboardingId, docTypeId, id } = match;
        const responseData = await this.newRepApplicationService
          .deletedocument({ onboardingId, docTypeId, id })
          .then((response) => JSON.parse(response))
          .catch((error) => {
            console.error('Failed to delete other attachment', error);
            return null;
          });

        // Same rule as removeFile(): only clear the row once the backend
        // confirms, otherwise the file would reappear on the next load.
        if (responseData?.status !== HttpStatusCode.Ok) {
          this.snackBarService.openFailureSnackBar('We couldn\'t remove the attachment. Please try again');
          return;
        }

        this.viewIntroducerDetails = this.viewIntroducerDetails.filter(item => item !== match);
      }

      this.dropOtherAttachment(key);
    });
  }

  private dropOtherAttachment(key: string): void {
    this.formGroup.removeControl(key);
    this.formGroup.removeControl(`${key}fileName`);
    this.otherAttachmentKeys = this.otherAttachmentKeys.filter(item => item !== key);
  }

  private patchAttachments(attachments: any[]): void {
    if (!Array.isArray(attachments) || attachments.length === 0) {
      return;
    }

    const categoryNames = new Set(attachments.map(item => item?.onboardingDocumentType).filter(Boolean));

    const patch: { [key: string]: any } = {};
    categoryNames.forEach(categoryName => {
      const mapping = this.DOCUMENT_TYPE_CONTROL_MAP[categoryName as string];
      if (!mapping) return;

      const item = this.getLatestAttachmentByType(categoryName as string);
      if (!item) return;

      const controlNameControl = this.formGroup.get(mapping.controlName);
      const fileVarControl = this.formGroup.get(mapping.fileVar);

      if (controlNameControl && controlNameControl.pristine) {
        patch[mapping.controlName] = item.fileName;
      }
      if (fileVarControl && fileVarControl.pristine) {
        patch[mapping.fileVar] = { name: item.fileName };
      }
    });

    this.formGroup.patchValue(patch);
  }

  public removeFile(controlName: string, fileVar: string, categoryName: string): void {
    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to remove the attachement?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result?.data !== true) {
        return;
      }

      const match = this.getLatestAttachmentByType(categoryName);

      if (match) {
        const { onboardingId, docTypeId, id } = match;

        const responseData = await this.newRepApplicationService
          .deletedocument({ onboardingId, docTypeId, id })
          .then((response) => JSON.parse(response))
          .catch((error) => {
            console.error('Failed to delete document', error);
            return null;
          });

        if (responseData?.status !== HttpStatusCode.Ok) {
          this.snackBarService.openFailureSnackBar('We couldn\'t remove the attachment. Please try again');
          return;
        }

        this.viewIntroducerDetails = this.viewIntroducerDetails.filter(item => item !== match);

        if (categoryName === 'Process Flow Diagram') {
          this.leadProcessFlowDocId = null;
        }
        if (categoryName === 'Privacy Policy / Personal Data Policy') {
          this.privacyPolicyDocId = null;
        }
      }

      this.formGroup.get(controlName)?.reset();
      const fileInput = document.getElementById(controlName) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      this.formGroup.get(fileVar)?.reset();
      this.formGroup.controls[controlName].markAsTouched();
      this.formGroup.controls[controlName].updateValueAndValidity();
    });
  }

  openSignatureDialog(): void {
    const dialogRef = this.dialog.open(SignatureDialogComponent, { width: '500px', disableClose: true });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.formGroup.controls['signature'].setValue(result);
      }
    });
  }

  get signatureValue() {
    return this.formGroup.controls['signature'].value;
  }

  private isAnyIntroducerRowEngaged(): boolean {
    return this.introducers.controls.some(row => !this.isIntroducerRowEmpty(row as FormGroup));
  }
  allowNumbersOnly(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;
    const input = event.target as HTMLInputElement;

    if (charCode === 43) {                         // '+'
      if (input.selectionStart !== 0 || input.value.includes('+')) {
        event.preventDefault();                // only allowed once, only at position 0
      }
      return;
    }

    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return;
    }

    if (input.value.length >= this.MAX_PHONE_LENGTH && input.selectionStart === input.selectionEnd) {
      event.preventDefault();
    }
  }

  sanitizeNumericInput(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const hasLeadingPlus = input.value.startsWith('+');
    const digits = input.value.replace(/\D/g, '');
    let sanitized = hasLeadingPlus ? '+' + digits : digits;

    if (sanitized.length > this.MAX_PHONE_LENGTH) {
      sanitized = sanitized.slice(0, this.MAX_PHONE_LENGTH);
    }

    if (sanitized !== input.value) {
      input.value = sanitized;
    }
    this.formGroup.get(controlName)?.setValue(sanitized);
  }
  clearSignature(): void {
    this.formGroup.controls['signature'].setValue(null);
    this.formGroup.controls['signature'].markAsTouched();
  }

  async previewAttachment(controlName: string, categoryName: string) {
    const match = this.getLatestAttachmentByType(categoryName);
    this.showPreview = true;
    if (match) {
      const { id } = match;
      // this.previewPdf(file.url);
      // console.log(id)

      try {

        const responseData = await this.newRepApplicationService
          .previewdocuments(id);

        // Create blob URL
        const fileURL = URL.createObjectURL(responseData);

        // Safe URL
        this.selectedDocument = this.sanitizer.bypassSecurityTrustResourceUrl(fileURL);
        this.isPdf = this.formGroup.get(controlName)?.value.toLowerCase().endsWith('.pdf');
        this.currentFileName = this.formGroup.get(controlName)?.value;

      } catch (error) {
        console.error(error);
      }
      this.resetZoom();
    }
  }

  async previewBeneficialOwnerFile(index: number) {
    const row = this.introducers.at(index) as FormGroup;
    const id = row.get('uploadedFileId')?.value;
    this.showPreview = true;
    if (id) {
      try {

        const responseData = await this.newRepApplicationService
          .previewdocuments(id);

        // Create blob URL
        const fileURL = URL.createObjectURL(responseData);

        // Safe URL
        this.selectedDocument = this.sanitizer.bypassSecurityTrustResourceUrl(fileURL);
        this.isPdf = row.get('uploadedFileName')?.value.toLowerCase().endsWith('.pdf');
        this.currentFileName = row.get('uploadedFileName')?.value;

      } catch (error) {
        console.error(error);
      }
      this.resetZoom();
    }

  }


  zoomIn(): void {
    this.zoomLevel = Math.min(this.MAX_ZOOM, +(this.zoomLevel + this.ZOOM_STEP).toFixed(2));
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.MIN_ZOOM, +(this.zoomLevel - this.ZOOM_STEP).toFixed(2));
  }

  resetZoom(): void {
    this.zoomLevel = 1;
  }

  closePreview() {
    this.showPreview = false;
    this.resetZoom();
  }
}